import { SportType } from 'modules/core/domain/enums/sport-type';
import { OrganizerListItemDataMapper } from 'modules/organizers/domain/data-mappers/organizer-list-item.data-mapper';
import { OrganizersDataMapper } from 'modules/organizers/domain/data-mappers/organizers.data-mapper';
import { OrganizerDetailsDto } from 'modules/organizers/domain/interfaces/organizer-details-dto';
import { OrganizerDetails } from 'modules/organizers/domain/interfaces/organizer-details';
import {
  OrganizerListItem,
  OrganizerListItemDto,
  OrganizersListResponseDto,
} from 'modules/organizers/domain/interfaces/organizer-list-item';
import { baseApi } from 'shared/api/base-api';

/**
 * Обе организации-запроса живут здесь: backend называет организацию
 * `tournament`, поэтому в GraphQL это `tournament(id:)` и `tournaments(input:)`,
 * а в домене — `OrganizerDetails` (карточка) и `OrganizerListItem` (строка списка).
 */

export interface ListOrganizersArgs {
  text?: string;
  phone?: string;
  sportType?: SportType;
  sortByCreatedAt?: string;
  sortBy?: string;
  limit: number;
  offset: number;
}

const GET_ORGANIZER_BY_ID_QUERY = `#graphql
  query getTournamentDetails($id: Int!) {
    tournament(id: $id) {
      id
      name
      sportType
      phone
      email
      createdAt
      updatedAt
      description
      images { type externalUrl createdAt }
      address { id city country }
      adminUser {
        id name surname middleName
        createdAt updatedAt
        wallet { balanceInMinorUnits }
      }
    }
  }
`;

const LIST_ORGANIZERS_QUERY = `#graphql
query listTournaments($input: TournamentsInput!) {
  tournaments(input: $input) {
    id
    name
    sportType
    phone
    email
    createdAt
    description
    images { type externalUrl createdAt }
    address { id city country }
    adminUser {
      id name surname middleName
      createdAt updatedAt
      wallet { balanceInMinorUnits }
    }
    subscriptionSummary {
      status
      activeTo
    }
  }
}
`;

const organizersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOrganizerById: build.query<OrganizerDetails, number>({
      query: (id) => ({
        query: GET_ORGANIZER_BY_ID_QUERY,
        variables: { id },
      }),
      transformResponse: (res: { tournament: OrganizerDetailsDto }) => new OrganizersDataMapper().decode([res.tournament])[0],
      providesTags: (_res, _err, id) => [{ type: 'Organizer', id }],
      keepUnusedDataFor: 120,
    }),

    listOrganizers: build.query<OrganizerListItem[], ListOrganizersArgs>({
      query: (args) => ({
        query: LIST_ORGANIZERS_QUERY,
        variables: {
          input: {
            text: args.text,
            phone: args.phone,
            sportType: args.sportType,
            sortByCreatedAt: args.sortByCreatedAt,
            sortBy: args.sortBy,
            limit: args.limit,
            offset: args.offset,
          },
        },
      }),
      transformResponse: (raw: OrganizersListResponseDto): OrganizerListItem[] => {
        const mapper = new OrganizerListItemDataMapper();
        return (raw.tournaments ?? []).map((dto: OrganizerListItemDto) => mapper.decode(dto));
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'Tournament' as const, id: item.id })),
              { type: 'Tournament' as const, id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'Tournament' as const, id: 'PARTIAL-LIST' }],
      keepUnusedDataFor: 60,
    }),
  }),
  overrideExisting: false,
});

export const { useGetOrganizerByIdQuery, useListOrganizersQuery } = organizersApi;
